'use client';
import React, { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FaqItem {
  q: string;
  a: string;
}

interface GuideSection {
  id: string;
  title: string;
  icon: string;
  color: string;
  articles: { title: string; content: string }[];
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const FAQ_ITEMS: FaqItem[] = [
  { q: 'How do I create a delivery order?', a: 'Go to "Create Order" from the sidebar. Fill in the parcel details, route, schedule, and suggested price. Once submitted, travelers on that route can place bids.' },
  { q: 'How does bidding work?', a: 'Travelers browse open orders and place bids with their proposed fee. As a sender, you review bids and accept the one that suits you best. The traveler is then assigned to your delivery.' },
  { q: 'When is payment released to the traveler?', a: 'Payment is held in escrow when you create an order. It is released to the traveler only after you confirm successful delivery. If there is a dispute, our team reviews the case.' },
  { q: 'How do I track my delivery?', a: 'Go to "Active Deliveries" in the sidebar. You can see real-time status updates, message your traveler, and view the delivery timeline.' },
  { q: 'What payment methods are supported?', a: 'We support M-Pesa, Bank Transfer, and Card/Stripe payments. You can set your default payment method in Profile → Preferences → Payment Defaults.' },
  { q: 'What is the 1.5% insurance fee?', a: 'MuyuRide automatically applies a 1.5% insurance fee on all delivery and storage payments. This fee goes into a dedicated Insurance Reserve Fund used to cover genuine losses — lost parcels, damage in transit, storage incidents, and non-delivery. It is mandatory and applied at checkout. Visit /insurance to learn more.' },
  { q: 'How do I file an insurance claim?', a: 'Go to Active Deliveries → open the affected delivery → click "Open Dispute". Select "Insurance Claim" as the dispute type. Provide photos, a description, and the declared value. Our team reviews within 48 hours and approved claims are paid within 5 business days.' },
  { q: 'How do I become a verified traveler?', a: 'Go to "Get Verified" in the sidebar or visit Profile → Verification. Upload your ID document and a selfie. Our team reviews submissions within 1–2 business days.' },
  { q: 'What is the trust score?', a: 'The trust score (out of 10) reflects your reliability based on completed deliveries, reviews, and dispute history. Higher scores make your profile more attractive to senders.' },
  { q: 'How do I open a dispute?', a: 'If there is an issue with a delivery, go to Active Deliveries, open the delivery, and click "Open Dispute". Provide details and evidence. Our moderation team will review within 24 hours.' },
  { q: 'Can I store parcels at a station?', a: 'Yes. Browse "Storage" in the sidebar to find available storage units at stations near you. You can book storage for short or long-term holds.' },
  { q: 'How do I contact support?', a: 'Use the live chat button at the bottom of this page to escalate to an admin. For non-urgent issues, browse the guides below or search the FAQ.' },
  { q: 'What currencies are supported?', a: 'MuyuRide supports KES, USD, GBP, EUR, INR, NGN, ZAR, KWD, AED, and SEK. You can set your preferred display currency in Profile → Preferences → Language & Currency.' },
  { q: 'How do I cancel an order?', a: 'You can cancel an order before a bid is accepted from the My Orders page. Once a bid is accepted and payment is in escrow, cancellation requires contacting support.' },
];

const GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'senders',
    title: 'Guide for Senders',
    icon: '📦',
    color: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
    articles: [
      { title: 'Creating your first order', content: 'Click "Create Order" in the sidebar. Step 1: Enter parcel details (category, weight, dimensions, fragility). Step 2: Set the route — origin city and destination city. Step 3: Choose your preferred travel date range. Step 4: Suggest a price or let travelers bid freely. Step 5: Review and submit. Your order is now live for travelers to bid on.' },
      { title: 'Managing bids on your order', content: 'Once your order is live, travelers will place bids. Go to "My Orders" → click your order → "View Bids". You can see each traveler\'s profile, trust score, and proposed fee. Accept the bid that works best for you. The traveler is immediately notified and payment moves to escrow.' },
      { title: 'Confirming delivery and releasing payment', content: 'When your parcel arrives, go to Active Deliveries and click "Confirm Delivery". This releases the escrowed payment to the traveler. If there is an issue, click "Open Dispute" instead — do not confirm delivery if the parcel has not arrived safely.' },
      { title: 'Leaving a review', content: 'After confirming delivery, you will be prompted to leave a review for the traveler. Honest reviews help build trust on the platform. Go to "Reviews" in the sidebar to see all your past reviews.' },
    ],
  },
  {
    id: 'travelers',
    title: 'Guide for Travelers',
    icon: '✈️',
    color: 'text-green-600 bg-green-500/10 border-green-500/20',
    articles: [
      { title: 'Publishing a trip', content: 'Go to "Publish Trip" in the sidebar. Enter your route (origin → destination), travel date, transport type, and available capacity (weight/volume). Your trip will be visible to senders with matching orders.' },
      { title: 'Browsing and bidding on orders', content: 'Go to "Marketplace" to see open orders matching your route. Click an order to view details. Click "Place Bid" to enter your proposed fee. You can bid on multiple orders. Once a sender accepts your bid, you are assigned to that delivery.' },
      { title: 'Managing active deliveries', content: 'Go to "Active Deliveries" to see all deliveries you are responsible for. Update the delivery status as you progress (picked up → in transit → delivered). Use the chat to communicate with the sender.' },
      { title: 'Getting paid', content: 'Payment is released to your linked bank account or M-Pesa once the sender confirms delivery. Go to "Earnings" to see your balance, pending payouts, and payout history. Add your payment details in Profile → Payment Methods.' },
    ],
  },
  {
    id: 'storage',
    title: 'Guide for Storage Hosts',
    icon: '🏪',
    color: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
    articles: [
      { title: 'Listing a storage unit', content: 'Go to "Storage" in the sidebar and click "Add Listing". Enter your unit details: location, size, price per day, available dates, and photos. Your listing will be visible to senders and travelers looking for temporary storage.' },
      { title: 'Managing bookings', content: 'When someone books your storage unit, you will receive a notification. Go to Storage → My Listings to see all active and upcoming bookings. You can approve or decline bookings based on availability.' },
      { title: 'Receiving payments', content: 'Storage payments are processed through the platform escrow. Once the booking period ends and the renter confirms, payment is released to your linked account. Add your payment details in Profile → Payment Methods.' },
    ],
  },
  {
    id: 'moderators',
    title: 'Guide for Station Moderators',
    icon: '🛡️',
    color: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
    articles: [
      { title: 'Moderator responsibilities', content: 'As a station moderator, you oversee deliveries passing through your station. You can verify parcel handoffs, flag suspicious activity, and escalate issues to admin. Your actions are logged in the audit trail.' },
      { title: 'Verifying parcel handoffs', content: 'When a traveler arrives at your station with a parcel, verify the parcel details match the order. Mark the handoff as verified in the Station Moderator panel. This updates the delivery status for both sender and traveler.' },
      { title: 'Escalating issues to admin', content: 'If you encounter a dispute, damaged parcel, or suspicious activity, use the "Escalate to Admin" button in the Station Moderator panel. Provide detailed notes. The admin team will review and take action within 2 hours.' },
    ],
  },
];

// ─── Live Chat Component ───────────────────────────────────────────────────────
function LiveChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: '1', from: 'admin', text: 'Hi! How can we help you today?', time: 'Just now' },
  ]);
  const [input, setInput] = useState('');
  const [sent, setSent] = useState(false);

  const sendMessage = () => {
    if (!input.trim()) return;
    setMessages((prev) => [...prev, { id: Date.now().toString(), from: 'user', text: input.trim(), time: 'Just now' }]);
    setInput('');
    setSent(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { id: (Date.now() + 1).toString(), from: 'admin', text: 'Thanks for reaching out! An admin will respond shortly. Typical response time is under 30 minutes.', time: 'Just now' }]);
    }, 1200);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open && (
        <div className="mb-3 w-80 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '420px' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />
              <span className="text-sm font-semibold">Live Support</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-white/20 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '280px' }}>
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs leading-relaxed ${msg.from === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
          {/* Input */}
          <div className="border-t border-border p-3 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message…"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button onClick={sendMessage} disabled={!input.trim()}
              className="p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
            </button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105">
        {open ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
        )}
        <span className="text-sm font-semibold">{open ? 'Close' : 'Live Chat'}</span>
        {!open && <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse" />}
      </button>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function HelpCenterContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGuide, setActiveGuide] = useState<string | null>(null);
  const [activeArticle, setActiveArticle] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'faq' | 'guides'>('faq');
  const router = useRouter();

  const filteredFaq = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_ITEMS;
    const q = searchQuery.toLowerCase();
    return FAQ_ITEMS.filter((item) => item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q));
  }, [searchQuery]);

  const filteredGuides = useMemo(() => {
    if (!searchQuery.trim()) return GUIDE_SECTIONS;
    const q = searchQuery.toLowerCase();
    return GUIDE_SECTIONS.map((section) => ({
      ...section,
      articles: section.articles.filter((a) => a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q)),
    })).filter((s) => s.articles.length > 0 || s.title.toLowerCase().includes(q));
  }, [searchQuery]);

  const selectedGuide = GUIDE_SECTIONS.find((g) => g.id === activeGuide);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-24">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-primary/20 via-primary/5 to-transparent rounded-2xl border border-border overflow-hidden p-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <button
            onClick={() => router.back()}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back
          </button>
          <h1 className="text-2xl font-bold text-foreground">Help Center</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-5">Find answers, read guides, or chat with our support team.</p>
          {/* Search */}
          <div className="relative max-w-xl">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQ, guides, topics…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {([{ id: 'faq', label: 'FAQ' }, { id: 'guides', label: 'Guides' }] as { id: typeof activeTab; label: string }[]).map((tab) => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setActiveGuide(null); setActiveArticle(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="space-y-3">
          {searchQuery && (
            <p className="text-xs text-muted-foreground">
              {filteredFaq.length} result{filteredFaq.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
            </p>
          )}
          {filteredFaq.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
              <p className="text-sm font-medium text-foreground">No results found</p>
              <p className="text-xs text-muted-foreground mt-1">Try a different search term or browse the guides below.</p>
            </div>
          ) : (
            filteredFaq.map((item, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/30 transition-colors">
                  <span className="text-sm font-semibold text-foreground pr-4">{item.q}</span>
                  <svg className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedFaq === i && (
                  <div className="px-5 pb-4 border-t border-border">
                    <p className="text-sm text-muted-foreground leading-relaxed pt-3">{item.a}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Guides Tab */}
      {activeTab === 'guides' && (
        <div>
          {/* Article detail view */}
          {activeGuide && selectedGuide && activeArticle !== null ? (
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-muted/30">
                <button onClick={() => setActiveArticle(null)} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div>
                  <p className="text-xs text-muted-foreground">{selectedGuide.title}</p>
                  <h2 className="text-sm font-semibold text-foreground">{selectedGuide.articles[activeArticle]?.title}</h2>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-muted-foreground leading-relaxed">{selectedGuide.articles[activeArticle]?.content}</p>
              </div>
            </div>
          ) : activeGuide && selectedGuide ? (
            /* Guide article list */
            <div className="space-y-3">
              <button onClick={() => setActiveGuide(null)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Back to Guides
              </button>
              <div className={`flex items-center gap-3 p-4 rounded-2xl border ${selectedGuide.color}`}>
                <span className="text-2xl">{selectedGuide.icon}</span>
                <h2 className="text-base font-bold">{selectedGuide.title}</h2>
              </div>
              <div className="space-y-2">
                {selectedGuide.articles.map((article, i) => (
                  <button key={i} onClick={() => setActiveArticle(i)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-card border border-border rounded-2xl hover:bg-muted/30 transition-colors text-left">
                    <span className="text-sm font-medium text-foreground">{article.title}</span>
                    <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Guide category grid */
            <div className="space-y-4">
              {searchQuery && (
                <p className="text-xs text-muted-foreground">
                  {filteredGuides.reduce((acc, g) => acc + g.articles.length, 0)} article{filteredGuides.reduce((acc, g) => acc + g.articles.length, 0) !== 1 ? 's' : ''} found
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(searchQuery ? filteredGuides : GUIDE_SECTIONS).map((section) => (
                  <button key={section.id} onClick={() => setActiveGuide(section.id)}
                    className={`flex items-start gap-4 p-5 rounded-2xl border text-left hover:scale-[1.01] transition-all ${section.color}`}>
                    <span className="text-3xl flex-shrink-0">{section.icon}</span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground">{section.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{section.articles.length} article{section.articles.length !== 1 ? 's' : ''}</p>
                      <div className="mt-2 space-y-0.5">
                        {section.articles.slice(0, 2).map((a, i) => (
                          <p key={i} className="text-xs text-muted-foreground truncate">• {a.title}</p>
                        ))}
                        {section.articles.length > 2 && <p className="text-xs text-muted-foreground">+{section.articles.length - 2} more…</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Escalation CTA */}
              <div className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Still need help?</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Our support team is online. Use the live chat button to escalate to an admin.</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-green-600 font-medium flex-shrink-0">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  Support online
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live Chat Widget */}
      <LiveChatWidget />
    </div>
  );
}
